<ng-container>
  <!-- Loading bar -->
  <fuse-loading-bar></fuse-loading-bar>

  <!-- Navigation -->
  @if (navigationService.navigation() && !layoutService.isAdminPage()) {
    <fuse-vertical-navigation
      class="print:hidden bg-light"
      [mode]="layoutService.isScreenSmall() ? 'over' : 'side'"
      [name]="'mainNavigation'"
      [navigation]="navigationService.filteredNavigation().default"
      [opened]="menuOpened()"
      (openedChanged)="saveNavigationStatus($event)"
      data-cy="main-navigation">
      <!-- Navigation header hook -->
      <ng-container fuseVerticalNavigationContentHeader>
        <div class="flex items-center w-full h-16 border-b relative top-px">
          <!-- Logo -->
          <div class="flex items-center justify-center w-full">
            <app-logo></app-logo>
          </div>
        </div>
      </ng-container>
    </fuse-vertical-navigation>
  }

  @if (navigationService.navigation && layoutService.isAdminPage()) {
    <fuse-vertical-navigation
      class="print:hidden bg-light"
      [mode]="layoutService.isScreenSmall() ? 'over' : 'side'"
      [name]="'adminNavigation'"
      [navigation]="navigationService.navigation().admin"
      [opened]="menuOpened()"
      data-cy="admin-navigation">
      <!-- Navigation header hook -->
      <ng-container fuseVerticalNavigationContentHeader>
        <div class="flex items-center w-full h-16 border-b relative top-px">
          <!-- Logo -->
          <div class="flex items-center justify-center w-full">
            <app-logo></app-logo>
          </div>
        </div>
      </ng-container>
    </fuse-vertical-navigation>
  }

  <!-- Wrapper -->
  <div class="flex flex-col flex-auto w-full min-w-0">
    <!-- Banner, header and page. `grow` takes up whatever the wrapper (at least one screen tall,
         html/body are min-h-full flex columns) leaves next to the footer: a short page pushes the
         powered-by footer to the bottom of the screen, a long one puts it right after the content.
         The block itself carries no min-height when the footer is shown — that put the footer
         under the fold on every page (#21752). Only a page that scrolls inside its own container
         is stretched to at least one screen: its `absolute inset-0` region needs that height. Such
         a page claims it with `OwnsScrollDirective` (module templates, and AdminComponent for the
         admin config pages), which puts `dd-owns-scroll` on its host from the first frame, so the
         `has-[.dd-owns-scroll]` class stretches the block before the planning board measures
         itself on init. shrink-0: the block may stretch past the screen, never squeeze under it;
         without it the footer ate 127px out of every page taller than the viewport. -->
    <div
      class="flex flex-col grow shrink-0 basis-auto w-full min-w-0 has-[.dd-owns-scroll]:min-h-[100dvh]"
      data-cy="layout-page-block">
      <app-impersonation-banner></app-impersonation-banner>
      <!-- Header -->
      <!-- `sticky top-0` keeps the bar on screen while a long page scrolls underneath it, the same
           as the modern shell (#22899 asks for both shells), so the icons on the right — the
           feedback button among them — stay reachable. It pins inside this page block, which spans
           the whole page, under the impersonation banner that scrolls away above it. `bg-default`
           is the colour the page already showed through the bar, now painted by the bar itself,
           opaque in both schemes, so the page never shows through. It stays a positioned box (what
           `relative` was for): `z-49` works the same. The shell publishes the bar's 64px as
           `--dd-sticky-header-offset` (sticky-header-offset.ts); both are one number. -->
      <div
        class="sticky top-0 flex flex-0 items-center w-full h-16 px-4 md:px-6 z-49 bg-default shadow dark:shadow-none dark:border-b print:hidden"
        [class.w-full]="isSidebarFullScreen"
        data-cy="layout-header">
        <!-- Navigation toggle button -->
        <button mat-icon-button (click)="toggleNavigation()" data-cy="toggle-main-navigation">
          <mat-icon [svgIcon]="'feather:menu'"></mat-icon>
        </button>
        <!-- title -->
        <div class="flex flex-col">
          <app-page-title [title]="layoutService.title()"></app-page-title>
        </div>

        <!-- icon menu (search, notifications, user) -->
        <app-icon-menu-items class="flex items-center pl-2 ml-auto space-x-0.5 sm:space-x-2"></app-icon-menu-items>
      </div>

      <!-- Content -->
      <div class="flex flex-col flex-auto">
        <!-- *ngIf="true" hack is required here for router-outlet to work correctly.
      Otherwise, layout changes won't be registered and the view won't be updated! -->
        @if (true) {
          <router-outlet></router-outlet>
        }
      </div>
    </div>

    <!-- Footer — hidden wherever the page scrolls inside its own container instead of with the
         document: a footer below that detaches from the inner scroll (it sticks while the content
         scrolls, #21262). `hideFooter` folds both sources of that fact — the admin config slug and
         a module template's own `ddOwnsScroll`. Kept invisible (not removed, so nothing shifts) until the
         page has settled after a navigation: the page block is as tall as its content, so the footer
         would otherwise sit right under the header while the content loads, then jump down
         (`LayoutService.pageSettled`). `motion-reduce` drops the fade, the reveal is then instant. -->
    @if (!layoutService.hideFooter()) {
      <footer
        class="shrink-0 print:hidden transition-opacity duration-200 motion-reduce:transition-none"
        [class.invisible]="!layoutService.pageSettled()"
        [class.opacity-0]="!layoutService.pageSettled()"
        data-cy="layout-footer">
        <dd-powered-by
          [name]="systemSettingsStore.branding()?.poweredByName ?? null"
          [logoLightSrc]="systemSettingsStore.branding()?.light?.poweredByLogo?.blobUrl ?? null"
          [logoDarkSrc]="systemSettingsStore.branding()?.dark?.poweredByLogo?.blobUrl ?? null"
          [url]="systemSettingsStore.branding()?.poweredByUrl ?? null"
          [logoHeight]="poweredByLogoHeight()"></dd-powered-by>
      </footer>
    }
  </div>
</ng-container>

<!-- Quick chat -->
<!--<quick-chat  #quickChat="quickChat"></quick-chat>-->
